import type { Project, WindowLayout } from '@cinegrid/core';
import { syncLivePlayhead } from '../engine/livePlayhead';
import { getMediaBlob, putMediaBlob, setRemoteMediaFetcher } from '../media/mediaStore';
import { saveProjectSnapshot } from '../media/projectStore';
import { useEditorStore } from '../state/editorStore';

const CHANNEL = 'cinegrid-editor-v1';
const HEARTBEAT_MS = 1500;
const LEADER_TIMEOUT_MS = 5000;
const CLAIM_GRACE_MS = 400;
const SECONDARY_CLAIM_MS = 2200;
const STATE_RETRY_MS = 800;
const PLAYHEAD_MIN_MS = 50;
const PROJECT_PATCH_MIN_MS = 80;

type SyncMessage =
  | { type: 'hello'; tabId: string; label: string; layoutId: string; isLeader: boolean }
  | { type: 'heartbeat'; tabId: string; label: string; layoutId: string; isLeader: boolean }
  | { type: 'leader-claim'; tabId: string; layoutId: string }
  | { type: 'leader-yield'; tabId: string }
  | { type: 'project-patch'; tabId: string; project: Project; playhead: number }
  | { type: 'playhead'; tabId: string; playhead: number; isPlaying: boolean }
  | { type: 'layout-changed'; tabId: string; layout: WindowLayout }
  | { type: 'request-state'; tabId: string }
  | {
      type: 'full-state';
      tabId: string;
      project: Project;
      playhead: number;
      isPlaying: boolean;
      selection?: string[];
    }
  | { type: 'selection'; tabId: string; selection: string[] }
  | { type: 'request-media'; tabId: string; assetId: string }
  | {
      type: 'media-blob';
      tabId: string;
      assetId: string;
      mimeType: string;
      name: string;
      buffer: ArrayBuffer;
    }
  | {
      type: 'media-chunk';
      tabId: string;
      assetId: string;
      mimeType: string;
      name: string;
      index: number;
      total: number;
      byteLength: number;
      buffer: ArrayBuffer;
    }
  | { type: 'media-missing'; tabId: string; assetId: string };

/** BroadcastChannel struggles with multi‑MB structured clones — send in pieces. */
const MEDIA_CHUNK_BYTES = 256 * 1024;

interface Peer {
  tabId: string;
  label: string;
  layoutId: string;
  isLeader: boolean;
  lastSeen: number;
}

function isPrimaryLayout(layoutId: string) {
  return layoutId === 'primary';
}

export function projectWeight(p: Project) {
  return p.assets.length * 1000 + p.clips.length + (p.settings?.duration ? 1 : 0);
}

function sameSelection(a: string[], b: string[]) {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

/**
 * Cross-tab sync: one shared project.
 * Primary editor prefers leadership; secondary tabs follow + may send grade patches.
 */
export class TabSync {
  private channel: BroadcastChannel | null = null;
  private peers = new Map<string, Peer>();
  private heartbeatTimer: number | null = null;
  private claimTimer: number | null = null;
  private retryTimer: number | null = null;
  private unsubStore: (() => void) | null = null;
  private applyingRemote = false;
  private lastSentProjectAt = 0;
  private lastSentPlayheadAt = 0;
  private knownLeaderId: string | null = null;
  private mediaWaiters = new Map<
    string,
    {
      resolve: (
        v: { buffer: ArrayBuffer; mimeType: string; name: string } | null
      ) => void;
      timer: number;
    }
  >();
  private mediaChunks = new Map<
    string,
    {
      mimeType: string;
      name: string;
      total: number;
      byteLength: number;
      parts: (ArrayBuffer | null)[];
    }
  >();
  private lastPushedAssetIds = new Set<string>();
  private pushMediaTimer: number | null = null;
  private persistTimer: number | null = null;

  start() {
    if (typeof BroadcastChannel === 'undefined') {
      useEditorStore.getState().setLeader(true);
      return;
    }
    this.channel = new BroadcastChannel(CHANNEL);
    this.channel.onmessage = (ev) => this.onMessage(ev.data as SyncMessage);
    setRemoteMediaFetcher((assetId) => this.requestMedia(assetId));

    useEditorStore.getState().setLeader(false);

    const state = useEditorStore.getState();
    this.post({
      type: 'hello',
      tabId: state.tabId,
      label: state.layout.label,
      layoutId: state.layout.id,
      isLeader: false,
    });
    this.requestState();

    const claimMs = isPrimaryLayout(state.layout.id) ? CLAIM_GRACE_MS : SECONDARY_CLAIM_MS;
    this.claimTimer = window.setTimeout(() => this.tryClaimLeader(), claimMs);
    this.heartbeatTimer = window.setInterval(() => this.heartbeat(), HEARTBEAT_MS);
    this.retryTimer = window.setInterval(() => this.maybeRetryStatePull(), STATE_RETRY_MS);

    let prevProject = state.project;
    let prevPlayhead = state.playhead;
    let prevPlaying = state.isPlaying;
    let prevSelection = state.selection;

    this.unsubStore = useEditorStore.subscribe((s) => {
      if (this.applyingRemote) return;

      if (s.project !== prevProject) {
        prevProject = s.project;
        // Debounce disk writes — Color Suite opens shouldn't stall on IDB
        if (this.persistTimer) window.clearTimeout(this.persistTimer);
        this.persistTimer = window.setTimeout(() => {
          this.persistTimer = null;
          void saveProjectSnapshot(useEditorStore.getState().project);
        }, 400);
        const now = Date.now();
        if (now - this.lastSentProjectAt > PROJECT_PATCH_MIN_MS) {
          this.lastSentProjectAt = now;
          if (s.isLeader) {
            this.broadcastFullState();
            this.schedulePushAllMedia();
          } else {
            this.post({
              type: 'project-patch',
              tabId: s.tabId,
              project: s.project,
              playhead: s.playhead,
            });
          }
        }
      }

      if (s.playhead !== prevPlayhead || s.isPlaying !== prevPlaying) {
        const playingChanged = s.isPlaying !== prevPlaying;
        prevPlayhead = s.playhead;
        prevPlaying = s.isPlaying;
        const now = Date.now();
        if (playingChanged || now - this.lastSentPlayheadAt >= PLAYHEAD_MIN_MS) {
          this.lastSentPlayheadAt = now;
          this.post({
            type: 'playhead',
            tabId: s.tabId,
            playhead: s.playhead,
            isPlaying: s.isPlaying,
          });
        }
      }

      if (!sameSelection(s.selection, prevSelection)) {
        prevSelection = s.selection;
        this.post({ type: 'selection', tabId: s.tabId, selection: s.selection });
      }
    });

    window.addEventListener('beforeunload', this.onUnload);
  }

  stop() {
    const state = useEditorStore.getState();
    if (state.isLeader) {
      this.post({ type: 'leader-yield', tabId: state.tabId });
    }
    window.removeEventListener('beforeunload', this.onUnload);
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    if (this.claimTimer) clearTimeout(this.claimTimer);
    if (this.retryTimer) clearInterval(this.retryTimer);
    if (this.pushMediaTimer) clearTimeout(this.pushMediaTimer);
    this.unsubStore?.();
    setRemoteMediaFetcher(null);
    for (const [, w] of this.mediaWaiters) {
      clearTimeout(w.timer);
      w.resolve(null);
    }
    this.mediaWaiters.clear();
    this.channel?.close();
  }

  requestMedia(
    assetId: string
  ): Promise<{ buffer: ArrayBuffer; mimeType: string; name: string } | null> {
    if (this.mediaWaiters.has(assetId)) {
      return new Promise((resolve) => {
        const prev = this.mediaWaiters.get(assetId)!;
        const orig = prev.resolve;
        prev.resolve = (v) => {
          orig(v);
          resolve(v);
        };
      });
    }

    return new Promise((resolve) => {
      const timer = window.setTimeout(() => {
        this.mediaWaiters.delete(assetId);
        resolve(null);
      }, 20000);
      this.mediaWaiters.set(assetId, { resolve, timer });
      this.post({
        type: 'request-media',
        tabId: useEditorStore.getState().tabId,
        assetId,
      });
    });
  }

  private onUnload = () => {
    const state = useEditorStore.getState();
    if (state.isLeader) {
      this.post({ type: 'leader-yield', tabId: state.tabId });
    }
  };

  private post(msg: SyncMessage) {
    try {
      this.channel?.postMessage(msg);
    } catch (err) {
      console.warn('tabSync post failed', err);
    }
  }

  private requestState() {
    this.post({ type: 'request-state', tabId: useEditorStore.getState().tabId });
  }

  private broadcastFullState() {
    const s = useEditorStore.getState();
    if (!s.isLeader) return;
    this.post({
      type: 'full-state',
      tabId: s.tabId,
      project: s.project,
      playhead: s.playhead,
      isPlaying: s.isPlaying,
      selection: s.selection,
    });
  }

  private maybeRetryStatePull() {
    const s = useEditorStore.getState();
    if (s.isLeader) return;
    // Only pull when we have nothing useful — constant full-state spam freezes Color Suite
    if (projectWeight(s.project) > 0) return;
    this.requestState();
  }

  private becomeLeader() {
    const s = useEditorStore.getState();
    if (!s.isLeader) useEditorStore.getState().setLeader(true);
    this.knownLeaderId = s.tabId;
    this.lastPushedAssetIds.clear();
    this.post({
      type: 'leader-claim',
      tabId: s.tabId,
      layoutId: s.layout.id,
    });
    this.broadcastFullState();
    this.schedulePushAllMedia();
  }

  private becomeFollower(leaderId: string | null) {
    const s = useEditorStore.getState();
    if (s.isLeader) useEditorStore.getState().setLeader(false);
    if (leaderId) this.knownLeaderId = leaderId;
    this.requestState();
  }

  private async fulfillMediaRequest(assetId: string, asLeader: boolean) {
    const payload = await this.loadAssetBytes(assetId);
    if (!payload) {
      if (asLeader) {
        this.post({
          type: 'media-missing',
          tabId: useEditorStore.getState().tabId,
          assetId,
        });
      }
      return;
    }
    await this.postMediaBytes(assetId, payload.mimeType, payload.name, payload.buffer);
  }

  private async loadAssetBytes(
    assetId: string
  ): Promise<{ buffer: ArrayBuffer; mimeType: string; name: string } | null> {
    const state = useEditorStore.getState();
    const asset = state.project.assets.find((a) => a.id === assetId);
    if (!asset) return null;
    if (asset.kind === 'adjustment' || asset.kind === 'solid') return null;
    if (!asset.url && asset.sizeBytes === 0) return null;

    let mimeType = asset.mimeType || 'application/octet-stream';
    let name = asset.name || assetId;

    const stored = await getMediaBlob(assetId);
    if (stored?.blob && stored.blob.size > 0) {
      return {
        buffer: await stored.blob.arrayBuffer(),
        mimeType: stored.mimeType || mimeType,
        name: stored.name || name,
      };
    }

    if (asset.url) {
      try {
        const res = await fetch(asset.url);
        const blob = await res.blob();
        if (blob.size === 0) return null;
        mimeType = asset.mimeType || blob.type || mimeType;
        await putMediaBlob(assetId, blob, mimeType, name);
        return { buffer: await blob.arrayBuffer(), mimeType, name };
      } catch {
        return null;
      }
    }
    return null;
  }

  private async postMediaBytes(
    assetId: string,
    mimeType: string,
    name: string,
    buffer: ArrayBuffer
  ) {
    const tabId = useEditorStore.getState().tabId;
    if (buffer.byteLength <= MEDIA_CHUNK_BYTES) {
      try {
        this.channel?.postMessage({
          type: 'media-blob',
          tabId,
          assetId,
          mimeType,
          name,
          buffer,
        } satisfies SyncMessage);
        return;
      } catch (err) {
        console.warn('tabSync media-blob failed, chunking', err);
      }
    }

    const total = Math.ceil(buffer.byteLength / MEDIA_CHUNK_BYTES);
    for (let index = 0; index < total; index++) {
      const start = index * MEDIA_CHUNK_BYTES;
      const slice = buffer.slice(start, start + MEDIA_CHUNK_BYTES);
      try {
        this.channel?.postMessage({
          type: 'media-chunk',
          tabId,
          assetId,
          mimeType,
          name,
          index,
          total,
          byteLength: buffer.byteLength,
          buffer: slice,
        } satisfies SyncMessage);
      } catch (err) {
        console.warn('tabSync media-chunk failed', assetId, index, err);
        return;
      }
    }
  }

  private schedulePushAllMedia() {
    if (this.pushMediaTimer) clearTimeout(this.pushMediaTimer);
    this.pushMediaTimer = window.setTimeout(() => {
      this.pushMediaTimer = null;
      void this.pushAllMedia();
    }, 120);
  }

  private async pushAllMedia() {
    const state = useEditorStore.getState();
    if (!state.isLeader) return;
    for (const asset of state.project.assets) {
      if (asset.kind === 'adjustment' || asset.kind === 'solid') continue;
      if (!asset.url && !(asset.sizeBytes > 0)) continue;
      if (this.lastPushedAssetIds.has(asset.id)) continue;
      this.lastPushedAssetIds.add(asset.id);
      const payload = await this.loadAssetBytes(asset.id);
      if (!payload) continue;
      await this.postMediaBytes(asset.id, payload.mimeType, payload.name, payload.buffer);
    }
  }

  private async ingestRemoteMedia(
    assetId: string,
    mimeType: string,
    name: string,
    buffer: ArrayBuffer
  ) {
    const blob = new Blob([buffer], { type: mimeType || 'application/octet-stream' });
    await putMediaBlob(assetId, blob, mimeType, name);
    const waiter = this.mediaWaiters.get(assetId);
    if (waiter) {
      clearTimeout(waiter.timer);
      this.mediaWaiters.delete(assetId);
      waiter.resolve({ buffer, mimeType, name });
    }
    window.dispatchEvent(
      new CustomEvent('cinegrid-media-ready', { detail: { assetId } })
    );
  }

  private onMediaChunk(
    msg: Extract<SyncMessage, { type: 'media-chunk' }>
  ) {
    let entry = this.mediaChunks.get(msg.assetId);
    if (!entry || entry.total !== msg.total || entry.byteLength !== msg.byteLength) {
      entry = {
        mimeType: msg.mimeType,
        name: msg.name,
        total: msg.total,
        byteLength: msg.byteLength,
        parts: Array.from({ length: msg.total }, () => null),
      };
      this.mediaChunks.set(msg.assetId, entry);
    }
    entry.parts[msg.index] = msg.buffer;
    if (entry.parts.some((p) => p == null)) return;

    const out = new Uint8Array(entry.byteLength);
    let offset = 0;
    for (const part of entry.parts) {
      const u8 = new Uint8Array(part!);
      out.set(u8, offset);
      offset += u8.byteLength;
    }
    this.mediaChunks.delete(msg.assetId);
    void this.ingestRemoteMedia(msg.assetId, entry.mimeType, entry.name, out.buffer);
  }

  private applyRemoteProject(
    fromTabId: string,
    project: Project,
    playhead: number,
    isPlaying?: boolean,
    selection?: string[],
    opts?: { force?: boolean }
  ) {
    const state = useEditorStore.getState();
    const remoteW = projectWeight(project);
    const localW = projectWeight(state.project);
    const fromPrimary = isPrimaryLayout(this.peers.get(fromTabId)?.layoutId ?? '');
    const fromLeader = fromTabId === this.knownLeaderId || !!this.peers.get(fromTabId)?.isLeader;

    // Never replace a filled project with an empty one (unless forced from primary)
    if (remoteW === 0 && localW > 0 && !opts?.force && !fromPrimary) return;

    if (state.isLeader && !opts?.force) {
      // Primary always yields to another primary with a heavier/newer project
      if (fromPrimary && !isPrimaryLayout(state.layout.id)) {
        useEditorStore.getState().setLeader(false);
        this.knownLeaderId = fromTabId;
      } else if (remoteW > localW || (project.updatedAt ?? 0) > (state.project.updatedAt ?? 0) + 50) {
        useEditorStore.getState().setLeader(false);
        this.knownLeaderId = fromTabId;
      } else if (!fromLeader && !fromPrimary) {
        return;
      } else {
        return;
      }
    }

    this.knownLeaderId = fromTabId;
    this.applyingRemote = true;
    useEditorStore.getState().setProject(project, { recordHistory: false });
    useEditorStore.getState().setPlayhead(playhead);
    if (isPlaying !== undefined) {
      useEditorStore.getState().setPlaying(isPlaying);
    }
    if (selection) {
      useEditorStore.getState().selectClips(selection, 'replace');
    }
    this.applyingRemote = false;
    void saveProjectSnapshot(project);

    for (const asset of project.assets) {
      if (asset.kind === 'adjustment' || asset.kind === 'solid') continue;
      if (!asset.url && !(asset.sizeBytes > 0)) continue;
      void getMediaBlob(asset.id).then((stored) => {
        if (stored?.blob && stored.blob.size > 0) return;
        void this.requestMedia(asset.id);
      });
    }
  }

  private acceptFollowerProjectPatch(fromTabId: string, project: Project, playhead: number) {
    const state = useEditorStore.getState();
    if (!state.isLeader) return;
    // Prefer newer edits from color / secondary windows
    if ((project.updatedAt ?? 0) < (state.project.updatedAt ?? 0) - 200) return;
    this.applyingRemote = true;
    useEditorStore.getState().setProject(project, { recordHistory: false });
    // Keep leader playhead unless follower scrubbed significantly while paused
    if (!state.isPlaying && Math.abs(playhead - state.playhead) > 0.05) {
      useEditorStore.getState().setPlayhead(playhead);
    }
    this.applyingRemote = false;
    void saveProjectSnapshot(project);
    this.lastSentProjectAt = Date.now();
    this.broadcastFullState();
    void fromTabId;
  }

  private notePeer(msg: {
    tabId: string;
    label?: string;
    layoutId: string;
    isLeader: boolean;
  }) {
    const prev = this.peers.get(msg.tabId);
    this.peers.set(msg.tabId, {
      tabId: msg.tabId,
      label: msg.label ?? prev?.label ?? '',
      layoutId: msg.layoutId,
      isLeader: msg.isLeader,
      lastSeen: Date.now(),
    });
    if (msg.isLeader) {
      const was = this.knownLeaderId;
      this.knownLeaderId = msg.tabId;
      if (was !== msg.tabId && !useEditorStore.getState().isLeader) {
        this.requestState();
      }
    }
    this.publishPeers();
  }

  private onMessage(msg: SyncMessage) {
    const state = useEditorStore.getState();
    if ('tabId' in msg && msg.tabId === state.tabId) return;

    switch (msg.type) {
      case 'hello':
        this.notePeer(msg);
        if (state.isLeader) {
          this.lastPushedAssetIds.clear();
          this.broadcastFullState();
          this.schedulePushAllMedia();
        }
        if (
          isPrimaryLayout(msg.layoutId) &&
          !isPrimaryLayout(state.layout.id) &&
          state.isLeader
        ) {
          this.becomeFollower(msg.isLeader ? msg.tabId : null);
        }
        break;
      case 'heartbeat':
        this.notePeer(msg);
        if (
          isPrimaryLayout(msg.layoutId) &&
          !isPrimaryLayout(state.layout.id) &&
          state.isLeader
        ) {
          this.becomeFollower(msg.isLeader ? msg.tabId : null);
        }
        break;
      case 'leader-claim': {
        this.notePeer({
          tabId: msg.tabId,
          layoutId: msg.layoutId,
          isLeader: true,
        });
        if (state.isLeader) {
          const theyPrimary = isPrimaryLayout(msg.layoutId);
          const wePrimary = isPrimaryLayout(state.layout.id);
          const lose =
            (theyPrimary && !wePrimary) ||
            (theyPrimary === wePrimary && state.tabId > msg.tabId);
          if (lose) this.becomeFollower(msg.tabId);
        } else {
          this.knownLeaderId = msg.tabId;
          this.requestState();
        }
        break;
      }
      case 'leader-yield':
        this.peers.delete(msg.tabId);
        if (this.knownLeaderId === msg.tabId) this.knownLeaderId = null;
        this.tryClaimLeader();
        this.publishPeers();
        break;
      case 'request-state':
        if (state.isLeader) {
          this.broadcastFullState();
          // Media is pushed on hello / becomeLeader — not on every state pull
        }
        break;
      case 'full-state':
        this.applyRemoteProject(
          msg.tabId,
          msg.project,
          msg.playhead,
          msg.isPlaying,
          msg.selection,
          { force: isPrimaryLayout(this.peers.get(msg.tabId)?.layoutId ?? '') }
        );
        break;
      case 'project-patch':
        if (state.isLeader) {
          this.acceptFollowerProjectPatch(msg.tabId, msg.project, msg.playhead);
        } else {
          this.applyRemoteProject(msg.tabId, msg.project, msg.playhead);
        }
        break;
      case 'playhead': {
        const fromLeader =
          msg.tabId === this.knownLeaderId || !!this.peers.get(msg.tabId)?.isLeader;
        if (state.isLeader) {
          const playingChanged = msg.isPlaying !== state.isPlaying;
          const scrubJump = Math.abs(msg.playhead - state.playhead) > 0.2;
          // Ignore follower playhead spam while already playing (leader owns the clock)
          if (!playingChanged && state.isPlaying && !scrubJump) break;
          this.applyingRemote = true;
          syncLivePlayhead(msg.playhead);
          useEditorStore.getState().setPlayhead(msg.playhead);
          useEditorStore.getState().setPlaying(msg.isPlaying);
          this.applyingRemote = false;
          this.post({
            type: 'playhead',
            tabId: state.tabId,
            playhead: msg.playhead,
            isPlaying: msg.isPlaying,
          });
        } else if (fromLeader || !this.knownLeaderId) {
          this.applyingRemote = true;
          syncLivePlayhead(msg.playhead);
          useEditorStore.getState().setPlayhead(msg.playhead);
          useEditorStore.getState().setPlaying(msg.isPlaying);
          this.applyingRemote = false;
        }
        break;
      }
      case 'selection':
        this.applyingRemote = true;
        useEditorStore.getState().selectClips(msg.selection, 'replace');
        this.applyingRemote = false;
        break;
      case 'layout-changed':
        this.notePeer({
          tabId: msg.tabId,
          label: msg.layout.label,
          layoutId: msg.layout.id,
          isLeader: this.peers.get(msg.tabId)?.isLeader ?? false,
        });
        this.tryClaimLeader();
        break;
      case 'request-media':
        void this.fulfillMediaRequest(msg.assetId, state.isLeader);
        break;
      case 'media-blob':
        void this.ingestRemoteMedia(msg.assetId, msg.mimeType, msg.name, msg.buffer);
        break;
      case 'media-chunk':
        this.onMediaChunk(msg);
        break;
      case 'media-missing': {
        const waiter = this.mediaWaiters.get(msg.assetId);
        if (waiter) {
          clearTimeout(waiter.timer);
          this.mediaWaiters.delete(msg.assetId);
          waiter.resolve(null);
        }
        break;
      }
    }
  }

  private heartbeat() {
    const state = useEditorStore.getState();
    const now = Date.now();
    for (const [id, peer] of this.peers) {
      if (now - peer.lastSeen > LEADER_TIMEOUT_MS) {
        this.peers.delete(id);
        if (peer.isLeader || this.knownLeaderId === id) {
          this.knownLeaderId = null;
          this.tryClaimLeader();
        }
      }
    }
    this.post({
      type: 'heartbeat',
      tabId: state.tabId,
      label: state.layout.label,
      layoutId: state.layout.id,
      isLeader: state.isLeader,
    });
    this.publishPeers();
  }

  private tryClaimLeader() {
    const state = useEditorStore.getState();

    const primaryPeer = [...this.peers.values()].find((p) =>
      isPrimaryLayout(p.layoutId)
    );
    if (!isPrimaryLayout(state.layout.id) && primaryPeer) {
      if (state.isLeader) this.becomeFollower(primaryPeer.isLeader ? primaryPeer.tabId : null);
      else this.requestState();
      return;
    }

    const peerLeader = [...this.peers.values()].find((p) => p.isLeader);
    if (peerLeader && peerLeader.tabId !== state.tabId) {
      const theyPrimary = isPrimaryLayout(peerLeader.layoutId);
      const wePrimary = isPrimaryLayout(state.layout.id);
      if (wePrimary && !theyPrimary) {
        this.becomeLeader();
        return;
      }
      this.becomeFollower(peerLeader.tabId);
      return;
    }

    this.becomeLeader();
  }

  private publishPeers() {
    const state = useEditorStore.getState();
    useEditorStore.getState().setConnectedTabs([
      {
        tabId: state.tabId,
        label: state.layout.label,
        layoutId: state.layout.id,
        isLeader: state.isLeader,
      },
      ...[...this.peers.values()].map((p) => ({
        tabId: p.tabId,
        label: p.label,
        layoutId: p.layoutId,
        isLeader: p.isLeader,
      })),
    ]);
  }

  announceLayout(layout: WindowLayout) {
    const state = useEditorStore.getState();
    this.post({ type: 'layout-changed', tabId: state.tabId, layout });
    this.tryClaimLeader();
  }
}

export const tabSync = new TabSync();
